import {
  areArraysEqual,
  calcNestedNodeId,
  convertExpandedNodesToGroupedNodeState,
  expandedNodesHasId,
} from '@/helpers/expandedNodesUtils';
import { spreadsheetSliceName } from '@/redux/constants';
import {
  addCollapsedNode,
  addExpandedNode,
  dropExpandedNode,
  pushExpandedNode,
  removeCollapsedNode,
  removeExpandedNode,
  setExpandedNodes,
  setGroupedNodeState,
  updateGroupedNodeState,
} from '@/redux/reducers/spreadsheetSlice';
import clsx from 'clsx';
import { useState } from 'react';
import { flushSync } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '../UI/Button/Button';
import Icon from '../UI/Icon/Icon';
import PopoverV2 from '../UI/Popover/PopoverV2';

const InlineGroupPopover = ({ node = {}, field = '', path = '' }) => {
  const dispatch = useDispatch();
  const { clientState } = useSelector((state) => state[spreadsheetSliceName]);
  const columnState = clientState.groupedNodeState?.[field] || {};
  const { defaultExpanded, collapsedNodes = [], expandedNodes = [] } = columnState;

  const expanded = !node?.expanded;
  const isExpandAllDisabled = defaultExpanded && collapsedNodes.length === 0;
  const isCollapseDisabled = !defaultExpanded && expandedNodes.length === 0;

  const handleNodeStateUpdate = () => {
    if (expanded) {
      if (clientState.groupedNodeState?.[field]?.defaultExpanded) {
        flushSync(() => dispatch(removeCollapsedNode({ field, path })));
      } else {
        flushSync(() => dispatch(addExpandedNode({ field, path })));
      }
    } else {
      if (clientState.groupedNodeState?.[field]?.defaultExpanded) {
        flushSync(() => dispatch(addCollapsedNode({ field, path })));
      } else {
        flushSync(() => dispatch(removeExpandedNode({ field, path })));
      }
    }
  };

  const handleExpandGroup = () => {
    if (clientState.expandedNodes?.length === 0) {
      const groupedNodeState = clientState.groupedNodeState?.[field];

      if (groupedNodeState) {
        const isCurrentlyExpanded = groupedNodeState.defaultExpanded
          ? !groupedNodeState.collapsedNodes.some((p) => areArraysEqual(p, path))
          : groupedNodeState.expandedNodes.some((p) => areArraysEqual(p, path));

        if (isCurrentlyExpanded === expanded) return;
      }

      handleNodeStateUpdate(node, expanded);
    } else {
      const nestedNodeID = calcNestedNodeId(node, node.key);

      if (expanded && !expandedNodesHasId(nestedNodeID, clientState.expandedNodes)) {
        flushSync(() => dispatch(pushExpandedNode({ nestedNodeID, field: node.field })));
      } else if (!expanded && expandedNodesHasId(nestedNodeID, clientState.expandedNodes)) {
        flushSync(() => dispatch(dropExpandedNode(nestedNodeID)));
      }
    }
  };

  const handleExpandAll = () => {
    if (clientState?.expandedNodes?.length) {
      const convertedGroupedNodeState = convertExpandedNodesToGroupedNodeState(clientState);
      flushSync(() => {
        dispatch(setGroupedNodeState(convertedGroupedNodeState));
        dispatch(setExpandedNodes([]));
      });
    }
    dispatch(
      updateGroupedNodeState({
        field,
        update: { defaultExpanded: true, collapsedNodes: [], expandedNodes: [] },
      })
    );
  };

  const handleCollapseAll = () => {
    if (clientState.expandedNodes.length) {
      const convertedGroupedNodeState = convertExpandedNodesToGroupedNodeState(clientState);
      flushSync(() => {
        dispatch(setGroupedNodeState(convertedGroupedNodeState));
        dispatch(setExpandedNodes([]));
      });
    }
    dispatch(
      updateGroupedNodeState({
        field,
        update: { defaultExpanded: false, collapsedNodes: [], expandedNodes: [] },
      })
    );
  };

  const groupActions = [
    {
      icon: 'arrows-out-simple',
      label: expanded ? 'Expand Group' : 'Collapse Group',
      action: handleExpandGroup,
      disabled: false,
    },
    {
      icon: 'arrows-out-simple',
      label: 'Expand All',
      action: handleExpandAll,
      disabled: isExpandAllDisabled,
    },
    {
      icon: 'arrows-in-simple',
      label: 'Collapse All',
      action: handleCollapseAll,
      disabled: isCollapseDisabled,
    },
  ];

  return (
    <div className='relative flex flex-col p-1 bg-white rounded z-100'>
      {groupActions.map(({ icon, label, action, disabled }) => {
        return (
          <Button
            key={label}
            iconName={icon}
            onClick={action}
            disabled={disabled}
            size='small'
            className='!w-full !justify-start !gap-1 !h-8'
            variant='ghost'
          >
            {label}
          </Button>
        );
      })}
    </div>
  );
};

const CustomGroupMenu = ({ node = {}, field = '', path = '' }) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  return (
    <div className='absolute top-1 right-1'>
      <PopoverV2
        open={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        popoverClassName='w-[250px] border'
        align='start'
        content={<InlineGroupPopover node={node} field={field} path={path} />}
      >
        <Button
          size='small'
          className={clsx('!w-6 !h-6 !p-0 !m-0', isPopoverOpen && 'bg-shadow-200')}
          variant='ghost'
          onClick={setIsPopoverOpen}
        >
          <Icon name='dots-three' />
        </Button>
      </PopoverV2>
    </div>
  );
};

export default CustomGroupMenu;
